import { useSuspenseQuery } from "@tanstack/react-query";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import {
  AlertDetailsAffectedEndpoints,
  AlertDetailsAffectedPolicies,
  AlertDetailsAffectedUsers,
  AlertTypes,
  assertNever,
  ControlCode,
  getControlName,
  queries,
  useAuthConfig,
} from "@theprelude/core";
import {
  ControlIcon,
  ExceptionIcon,
  Overlay,
  OverlayLoading,
  Truncate,
} from "@theprelude/ds";
import merge from "lodash/merge";
import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { z } from "zod";
import { useOverlay } from "@/hooks/use-overlay";
import { OverlayErrorFallback } from "../default-overlay";
import { ResponsiveContainer } from "../responsive-container";
import {
  Column,
  endpointHostnameColumn,
  endpointSerialColumn,
  policyArrowColumn,
  policyColumn,
  policyPlatformColumn,
  policySettingNameColumn,
  policySettingRecommendationColumn,
  policySettingValueColumn,
  userEmailColumn,
} from "../table/columns";
import { useTable } from "../table/hooks";
import { None } from "../table/unavaliable";
import { VirtualTable } from "../table/virtual-table";
import { getAlertIcon, getAlertMessage } from "./alert";

export const alertSchema = z.tuple([
  z.literal("alert"),
  z.object({ id: z.string() }),
]);

export type AlertSchema = z.infer<typeof alertSchema>[1];

export function AlertOverlay(props: AlertSchema) {
  const overlay = useOverlay();

  return (
    <Overlay
      title={
        <Suspense fallback={<TitleLoading />}>
          <Title {...props} />
        </Suspense>
      }
      position="right"
      defaultSize={60}
      hideOverlay={overlay.close}
    >
      <ErrorBoundary FallbackComponent={OverlayErrorFallback}>
        <Suspense fallback={<OverlayLoading />}>
          <div className="flex h-full flex-col gap-4 p-4 pb-0">
            <AlertsTable {...props} />
          </div>
        </Suspense>
      </ErrorBoundary>
    </Overlay>
  );
}

const Title = ({ id }: AlertSchema) => {
  const { data: alert } = useAlertQuery({ id });

  const icon = getAlertIcon(alert);
  const title = getAlertMessage(alert);

  return (
    <div className="flex items-center gap-2">
      {icon}
      {title}
    </div>
  );
};

const TitleLoading = () => (
  <div className="flex items-center gap-2">
    <ExceptionIcon />
    <div className="h-3 w-96 animate-pulse rounded-md bg-secondary-foreground" />
  </div>
);

const useAlertQuery = ({ id }: AlertSchema) => {
  const authConfig = useAuthConfig();
  return useSuspenseQuery({
    ...queries.detect.getAlerts(authConfig),
    select: (alerts) => {
      const alert = alerts.find((e) => e.id === id);
      if (!alert) {
        throw new Error("Alert not found");
      }
      return alert;
    },
  });
};

const AlertsTable = ({ id }: AlertSchema) => {
  const { data: alert } = useAlertQuery({ id });

  try {
    switch (alert.alert_type) {
      case AlertTypes.NEW_NO_ASSET_MANAGER_ENDPOINTS:
      case AlertTypes.NEW_NO_EDR_ENDPOINTS:
      case AlertTypes.NEW_NO_VULN_MANAGER_ENDPOINTS:
      case AlertTypes.NEW_MISSING_AV_POLICY_ENDPOINTS:
      case AlertTypes.NEW_MISSING_EDR_POLICY_ENDPOINTS:
      case AlertTypes.NEW_MISSING_SCAN_ENDPOINTS:
      case AlertTypes.NEW_OUT_OF_DATE_SCAN_ENDPOINTS:
      case AlertTypes.NEW_REDUCED_FUNCTIONALITY_MODE_ENDPOINTS: {
        return <AffectedEndpointsTable data={alert.details.affected ?? []} />;
      }
      case AlertTypes.NEW_MISSING_MFA_USERS:
      case AlertTypes.NEW_USER_MISSING_ASSET_MANAGER:
      case AlertTypes.NEW_USER_MISSING_EDR:
      case AlertTypes.NEW_USER_MISSING_VULN_MANAGER: {
        return <AffectedUsersTable data={alert.details.affected ?? []} />;
      }
      case AlertTypes.NEW_POLICY_SETTING_PASS:
      case AlertTypes.NEW_POLICY_SETTING_FAILURE: {
        return <AffectedPoliciesTable data={alert.details.affected ?? []} />;
      }
      default: {
        return assertNever(alert);
      }
    }
  } catch {
    return <div>Unknown alert type.</div>;
  }
};

type AffectedUser = NonNullable<AlertDetailsAffectedUsers["affected"]>[number];

type AffectedEndpoint = NonNullable<
  AlertDetailsAffectedEndpoints["affected"]
>[number];

type AffectedPolicy = NonNullable<
  AlertDetailsAffectedPolicies["affected"]
>[number];

const controlColumn: Column<ControlCode> = {
  id: "control",
  enableHiding: true,
  enableSorting: true,
  sortUndefined: "last",
  cell: (info) => {
    const value = info.getValue();
    if (value === null) {
      return <None />;
    }
    const name = getControlName(value);
    return (
      <div className="flex items-center gap-1">
        <ControlIcon control={value} />
        <Truncate text={name}>{name}</Truncate>
      </div>
    );
  },
  meta: {
    label: "Control",
    widths: ["150px", "1fr"],
    canExport: true,
  },
};

const endpointColumnHelper = createColumnHelper<AffectedEndpoint>();
const endpointColumns = [
  endpointColumnHelper.accessor("control", controlColumn),
  endpointColumnHelper.accessor("hostname", endpointHostnameColumn),
  endpointColumnHelper.accessor("serial_number", endpointSerialColumn),
];

const userColumnHelper = createColumnHelper<AffectedUser>();
const userColumns = [
  userColumnHelper.accessor("control", controlColumn),
  userColumnHelper.accessor("email", userEmailColumn),
];

const policyColumnHelper = createColumnHelper<AffectedPolicy>();
const policyColumns = [
  policyColumnHelper.accessor("control", controlColumn),
  policyColumnHelper.accessor("policy_name", policyColumn),
  policyColumnHelper.accessor("platform", policyPlatformColumn),
  policyColumnHelper.accessor(
    "setting_name",
    merge(policySettingNameColumn, { meta: { label: "Setting" } }),
  ),
  policyColumnHelper.accessor("value", policySettingValueColumn),
  policyColumnHelper.display(policyArrowColumn),
  policyColumnHelper.accessor(
    "recommendation",
    policySettingRecommendationColumn,
  ),
];

const AffectedUsersTable = ({ data }: { data: AffectedUser[] }) => {
  const sortedData = sortBy(data, ["control", "email"]);
  return <AffectedResourcesTable data={sortedData} columns={userColumns} />;
};

const AffectedEndpointsTable = ({ data }: { data: AffectedEndpoint[] }) => {
  const sortedData = sortBy(data, ["control", "hostname", "serial_number"]);
  return <AffectedResourcesTable data={sortedData} columns={endpointColumns} />;
};

const AffectedPoliciesTable = ({ data }: { data: AffectedPolicy[] }) => {
  const sortedData = sortBy(data, [
    "control",
    "policy_name",
    "platform",
    "setting_name",
  ]);
  return <AffectedResourcesTable data={sortedData} columns={policyColumns} />;
};

const sortBy = <T,>(items: T[], keys: (keyof T)[]): T[] => {
  return items.sort((a, b) =>
    keys.reduce((result, key) => result || compare(a[key], b[key]), 0),
  );
};

const compare = <T,>(a: T, b: T): number => {
  if (typeof a === "string" && typeof b === "string") {
    return a.localeCompare(b);
  }
  return a === b ? 0 : a < b ? -1 : 1;
};

const AffectedResourcesTable = <T,>({
  data,
  columns,
}: {
  data: T[];
  columns: ColumnDef<T, any>[];
}) => {
  const table = useTable({
    name: "affected-alert-resources",
    columns,
    data,
  });

  return (
    <ResponsiveContainer className="flex-1 overflow-hidden">
      {({ height }) => (
        <VirtualTable
          table={table}
          height={height}
          emptyState={<span>No results.</span>}
        />
      )}
    </ResponsiveContainer>
  );
};
